import React, { useEffect, useState } from 'react'
import type { GetServerSideProps } from 'next'

import SEO from '@/components/SEO'
import FormSignin from '@/components/Forms/Signin'
import FormRequestNewPassword from '@/components/Forms/RequestPassword'
import FormResetPassword from '@/components/Forms/ResetPassword'
import Login from '@/components/Login'
import * as FormBoxStyles from '@/components/Login/FormBox/styles'

import Public from '@/helpers/PageTypes/Public'

import { useModal } from '@/contexts/ModalContext'

import { PAGE_PATHS } from '@/services/paths'
import { changeInputClass, validations } from '@/services/functions'
import {
  sendPostRequest,
  handleResponseStates,
  ENDPOINTS
} from '@/services/api'
import {
  invalidEmail,
  passwordTooShort,
  onTokenSent,
  onUserError,
  onUserNotFoundInstitutional,
  onTooManyRequests,
  onTooManyFailedAttempts,
  onUserNotFound,
  onLimitExceeded,
  onInvalidParameter,
  onUncaughtError
} from '@/services/userAlerts'

import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

const SCREEN_IDS = {
  login: 'login',
  forgot_password: 'forgot',
  change_password: 'change'
}

const MAX_LOGIN_ERROR_MODAL_ATTEMPTS = 3
const MIN_LOADING_VISIBLE_MS = 1000

const Signin = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const { callModal, MODAL_TYPES } = useModal()

  const [currentScreen, setCurrentScreen] = useState(SCREEN_IDS.login)

  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [rememberUser, setRememberUser] = useState(true)
  const [, setFailedLoginAttempts] = useState(0)

  const [loading, setLoading] = useState(false)
  const [isAlertFromReset, setIsAlertFromReset] = useState(null)

  let post = false
  let userNameValidation = false
  let passwordValidation = false

  useEffect(() => {
    changeInputClass('inp_email_login', userName, post, userNameValidation)
  }, [userName, post, userNameValidation])

  useEffect(() => {
    changeInputClass('inp_pass_login', password, post, passwordValidation)
  }, [password, post, passwordValidation])

  useEffect(() => {
    if (isAlertFromReset === null) return
    callModal(AlertSettings)
    setIsAlertFromReset(null)
  }, [isAlertFromReset])

  const validateSignInData = () => {
    post = true

    userNameValidation = validations.email(userName, 'inp_email_login', post)
    passwordValidation = validations.password(password, 'inp_pass_login', post)

    if (!userNameValidation) {
      invalidEmail()
      return false
    } else if (!passwordValidation) {
      passwordTooShort()
      return false
    }

    return true
  }

  const validateRequestPasswordData = () => {
    post = true

    userNameValidation = validations.email(
      userName,
      'inp_email_request_pass',
      post
    )

    if (!userNameValidation) {
      invalidEmail()
      return false
    }

    return true
  }

  const onSignIn = () => {
    setLoading(true)
    setFailedLoginAttempts(0)
    router.push(`${PAGE_PATHS.auth}?persist=${rememberUser}`)
  }

  const onInvalidCredentials = () => {
    setFailedLoginAttempts(previousAttempts => {
      const nextAttempts = previousAttempts + 1

      if (nextAttempts <= MAX_LOGIN_ERROR_MODAL_ATTEMPTS) {
        onUserError()
      } else {
        onUserNotFoundInstitutional()
      }

      return nextAttempts
    })
  }

  const onRequestedPassword = response => {
    onTokenSent(response.msg.Destination)
    setCurrentScreen(SCREEN_IDS.change_password)
  }

  const waitMinLoadingTime = async (loadingStartedAt: number) => {
    const elapsed = Date.now() - loadingStartedAt
    const remaining = MIN_LOADING_VISIBLE_MS - elapsed

    if (remaining > 0) {
      await new Promise(resolve => setTimeout(resolve, remaining))
    }
  }

  const responsesSignIn = [
    { name: 204, callback: onSignIn },
    { name: 'fail', callback: onInvalidCredentials },
    { name: 'NotAuthorizedException', callback: onInvalidCredentials },
    { name: 'InvalidPasswordException', callback: onInvalidCredentials },
    { name: 'UserNotFoundException', callback: onUserNotFound },
    { name: 'TooManyRequestsException', callback: onTooManyRequests },
    {
      name: 'TooManyFailedAttemptsException',
      callback: onTooManyFailedAttempts
    },
    { name: 'LimitExceededException', callback: onLimitExceeded },
    { name: 'InvalidParameterException', callback: onInvalidParameter },
    { name: 'UserNotConfirmed', callback: () => setIsAlertFromReset(false) }
  ]

  const responsesRequestPassword = [
    {
      name: 200,
      callback: onRequestedPassword
    },
    {
      name: 201,
      callback: () => {
        setIsAlertFromReset(true)
        setCurrentScreen(SCREEN_IDS.login)
      }
    },
    { name: 'UserNotFoundException', callback: onUserNotFound },
    { name: 'LimitExceededException', callback: onLimitExceeded }
  ]

  const signUserIn = async (email, pass, isAuto) => {
    if (!isAuto && !validateSignInData()) return

    const loadingStartedAt = Date.now()
    setLoading(true)

    await sendPostRequest(ENDPOINTS.sign_in, {
      username: email,
      password: pass
    })
      .then(async response => {
        await waitMinLoadingTime(loadingStartedAt)
        setLoading(false)
        handleResponseStates(response, responsesSignIn)
      })
      .catch(async error => {
        await waitMinLoadingTime(loadingStartedAt)
        setLoading(false)
        const msg = error.data || error
        console.error('signUserIn error: ', msg)
        onInvalidCredentials()
      })
  }

  const requestNewPassword = async () => {
    if (!validateRequestPasswordData()) return

    const loadingStartedAt = Date.now()
    setLoading(true)

    await sendPostRequest(ENDPOINTS.request_password, { username: userName })
      .then(async response => {
        await waitMinLoadingTime(loadingStartedAt)
        setLoading(false)
        handleResponseStates(response, responsesRequestPassword)
      })
      .catch(async error => {
        await waitMinLoadingTime(loadingStartedAt)
        setLoading(false)
        const msg = error.data || error
        console.error('requestNewPassword error: ', msg)
        onUncaughtError()
      })
  }

  const renderFormBoxFooter = () => {
    if (currentScreen !== SCREEN_IDS.forgot_password) return null

    return (
      <FormBoxStyles.FormBoxBackButton
        id="btn_back"
        type="button"
        onClick={() => setCurrentScreen(SCREEN_IDS.login)}
      >
        <span className="material-icons">arrow_back</span>
        <span>{t('buttons.return')}</span>
      </FormBoxStyles.FormBoxBackButton>
    )
  }

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case SCREEN_IDS.login:
        return (
          <FormSignin
            userNameState={userName}
            userNameHandler={setUserName}
            passwordState={password}
            passwordHandler={setPassword}
            rememberState={rememberUser}
            rememberHandler={setRememberUser}
            loginHandler={signUserIn}
            screenHandler={setCurrentScreen}
            screenIds={SCREEN_IDS}
          />
        )

      case SCREEN_IDS.forgot_password:
        return (
          <FormRequestNewPassword
            userNameState={userName}
            userNameHandler={setUserName}
            requestTokenHandler={requestNewPassword}
          />
        )

      case SCREEN_IDS.change_password:
        return (
          <FormResetPassword
            userNameState={userName}
            passwordHandler={setPassword}
            loginHandler={signUserIn}
            requestTokenHandler={requestNewPassword}
            loadHandler={setLoading}
            screenHandler={setCurrentScreen}
            screenIds={SCREEN_IDS}
          />
        )

      default:
        return
    }
  }

  const AlertSettings = {
    type: MODAL_TYPES.alert,
    config: {
      title: t('messages.confirmAccount'),
      text: (
        <>
          <p className="mb-1">
            {isAlertFromReset
              ? t('messages.notYetConfirmed1')
              : t('messages.notYetConfirmed2')}{' '}
            <b>{t('messages.notYetConfirmed3')}</b>
          </p>
          <p>{t('messages.notYetConfirmed4')}</p>
        </>
      ),
      img: {
        URL: `${process.env.CDN_URL}/public/alerts/email_person.webp`,
        width: 256,
        height: 219,
        alt: ''
      },
      circles: [
        { top: '35%', left: '6%', radius: 200, thickness: 40 },
        { top: '81%', left: '93%', radius: 120, thickness: 30 }
      ]
    }
  }

  return (
    <Public>
      <SEO title={` | ${t('titles.login')}`} />
      <div>
        <Login
          id="login-container"
          isLoading={loading}
          isResetPasswordScreen={currentScreen === SCREEN_IDS.forgot_password}
          loadingClass="form-login"
          formBoxContent={renderCurrentScreen()}
          formBoxFooter={renderFormBoxFooter()}
        />
      </div>
    </Public>
  )
}

export default Signin

export const getServerSideProps: GetServerSideProps = async ctx => {
  const { req, res } = ctx

  const hasVetRefresh = Boolean(req.cookies['__Secure-RefreshTokenVet'])
  const hasVetId = Boolean(req.cookies['__Secure-IdTokenVet'])
  const shouldRevokeVet = hasVetRefresh || hasVetId

  if (shouldRevokeVet) {
    try {
      const cookieHeader = req.headers.cookie ?? ''

      await fetch(`${process.env.API_URL}/${ENDPOINTS.sign_out}`, {
        method: 'GET',
        headers: {
          'X-Application': 'vet',
          cookie: cookieHeader
        },
        credentials: 'include'
      })

      ctx.res.setHeader('Set-Cookie', [
        `__Secure-RefreshTokenVet=; Max-Age=0; Path=/; Secure; HttpOnly; SameSite=None`,
        `__Secure-IdTokenVet=; Max-Age=0; Path=/; Secure; HttpOnly; SameSite=None`
      ])
      console.debug('[Signin SSR]', 'cleaned vet cookies.')
    } catch (e) {
      console.debug('SSR Vet revoke skipped/failure:', e)
    }
  }

  return { props: {} }
}
